/**
 * Prompt Template Utilities
 * Functions for working with prompt templates and variable replacement
 */

import logger from './logger';

/**
 * Extract variable names from a prompt template
 * Finds all {{variableName}} patterns
 */
export function extractVariables(template: string): string[] {
  const regex = /\{\{(\w+)\}\}/g;
  const variables: string[] = [];
  let match;

  while ((match = regex.exec(template)) !== null) {
    const varName = match[1];
    if (!variables.includes(varName)) {
      variables.push(varName);
    }
  }

  return variables;
}

/**
 * Replace variables in a template with actual values
 * @param template - The template string with {{variables}}
 * @param values - Object mapping variable names to values
 * @returns The template with variables replaced
 */
export function replaceVariables(
  template: string,
  values: Record<string, string>
): string {
  let result = template;

  Object.entries(values).forEach(([key, value]) => {
    const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
    result = result.replace(regex, value || '');
  });

  return result;
}

/**
 * Validate a prompt template
 * Checks for syntax errors and returns validation results
 */
export function validateTemplate(template: string): {
  valid: boolean;
  errors: string[];
  warnings: string[];
  variables: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check for empty template
  if (!template || template.trim().length === 0) {
    errors.push('Template cannot be empty');
  }

  // Check for unclosed braces
  const openBraces = (template.match(/\{\{/g) || []).length;
  const closeBraces = (template.match(/\}\}/g) || []).length;

  if (openBraces !== closeBraces) {
    errors.push('Unclosed variable braces found');
  }

  // Check for invalid variable names (should be alphanumeric + underscore)
  const invalidVars = template.match(/\{\{([^\w}]+)\}\}/g);
  if (invalidVars) {
    errors.push(`Invalid variable names: ${invalidVars.join(', ')}`);
  }

  // Extract variables
  const variables = extractVariables(template);

  // Check for single-brace (common mistake)
  if (template.includes('{') && !template.includes('{{')) {
    warnings.push('Found single braces - did you mean to use {{variable}}?');
  }

  // Check for very long template (>10000 chars)
  if (template.length > 10000) {
    warnings.push('Template is very long and may exceed token limits');
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    variables
  };
}

/**
 * Get missing variables (variables in template not provided in values)
 */
export function getMissingVariables(
  template: string,
  values: Record<string, string>
): string[] {
  const templateVars = extractVariables(template);
  return templateVars.filter(varName => !(varName in values));
}

/**
 * Preview template with example values
 * Replaces variables and shows the result
 */
export function previewTemplate(
  template: string,
  values: Record<string, string>
): string {
  const missing = getMissingVariables(template, values);

  // Fill missing variables with placeholder text
  const allValues = { ...values };
  missing.forEach(varName => {
    allValues[varName] = `[${varName}]`;
  });

  return replaceVariables(template, allValues);
}

/**
 * Sanitize input values before template replacement
 * Prevents potential injection attacks
 */
export function sanitizeValue(value: string): string {
  // Remove any potential code injection attempts
  // For now, just trim and ensure it's a string
  return String(value || '').trim();
}

/**
 * Create a safe prompt from template and values
 * Combines validation, sanitization, and replacement
 */
export function createPromptFromTemplate(
  template: string,
  values: Record<string, string>,
  options: {
    allowMissing?: boolean;
    sanitize?: boolean;
  } = {}
): {
  success: boolean;
  prompt?: string;
  error?: string;
  missingVars?: string[];
} {
  try {
    // Validate template
    const validation = validateTemplate(template);
    if (!validation.valid) {
      return {
        success: false,
        error: validation.errors.join('; ')
      };
    }

    // Check for missing variables
    const missing = getMissingVariables(template, values);
    if (missing.length > 0 && !options.allowMissing) {
      return {
        success: false,
        error: 'Missing required variables',
        missingVars: missing
      };
    }

    // Sanitize values if requested
    const processedValues = options.sanitize
      ? Object.fromEntries(
          Object.entries(values).map(([k, v]) => [k, sanitizeValue(v)])
        )
      : values;

    // Replace variables
    const prompt = replaceVariables(template, processedValues);

    logger.debug('[PromptTemplate] Created prompt:', {
      templateLength: template.length,
      promptLength: prompt.length,
      variablesUsed: Object.keys(values).length
    });

    return {
      success: true,
      prompt
    };
  } catch (error) {
    logger.error('[PromptTemplate] Failed to create prompt:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Generate example template for user guidance
 */
export function getExampleTemplate(): string {
  return `Analyze the following content:

{{content}}

Focus on the following aspects:
- {{aspect1}}
- {{aspect2}}

Provide recommendations and insights.`;
}

/**
 * Generate variable name from label
 * Converts "My Variable" to "myVariable"
 */
export function generateVariableName(label: string): string {
  return label
    .trim()
    .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special chars
    .split(/\s+/)
    .map((word, index) => {
      const lower = word.toLowerCase();
      return index === 0 ? lower : lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
}
